/**
 * OnboardingStatusCard — P2.C onboarding gap #4 dealer-floor traffic light.
 *
 * RED   blocks handover; "DO NOT RELEASE" banner + reason list.
 * AMBER signals pending; lists what's missing so the dealer knows
 *       whether to wait one more snapshot tick or to fix a policy.
 * GREEN clears the dealer; "READY TO RELEASE" + green badge.
 *
 * The owning page polls every 15s so a freshly-enrolled device flips
 * through RED → AMBER → GREEN without a manual refresh.
 */
import type { OnboardingStatus } from "@dfp/api-client";

interface Props {
  readonly loading: boolean;
  readonly error: boolean;
  readonly status: OnboardingStatus | undefined;
  readonly enrollmentVerifiedAt: string | null;
  readonly enrolledManufacturer: string | null;
  readonly enrolledModel: string | null;
}

export function OnboardingStatusCard({
  loading,
  error,
  status,
  enrollmentVerifiedAt,
  enrolledManufacturer,
  enrolledModel,
}: Props): JSX.Element {
  const tone: "red" | "amber" | "green" | "neutral" = status?.traffic ?? "neutral";
  const headerBg =
    tone === "red"
      ? "bg-red-50 border-red-300"
      : tone === "amber"
        ? "bg-amber-50 border-amber-300"
        : tone === "green"
          ? "bg-emerald-50 border-emerald-300"
          : "bg-slate-50 border-slate-200";
  const headerText =
    tone === "red"
      ? "text-red-800"
      : tone === "amber"
        ? "text-amber-800"
        : tone === "green"
          ? "text-emerald-800"
          : "text-slate-700";
  const label =
    tone === "red"
      ? "DO NOT RELEASE"
      : tone === "amber"
        ? "PENDING — wait"
        : tone === "green"
          ? "READY TO RELEASE"
          : "Checking…";

  return (
    <div className={`rounded-lg border px-4 py-3 ${headerBg}`}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className={`text-sm font-bold uppercase tracking-wide ${headerText}`}>
          DPC onboarding · {label}
        </h2>
        <span className="text-[10px] text-slate-500">refreshes every 15s</span>
      </div>
      {loading ? (
        <p className="mt-1 text-xs text-slate-500">Loading status…</p>
      ) : error ? (
        <p className="mt-1 text-xs text-red-700">Failed to load onboarding status.</p>
      ) : status ? (
        <>
          <ul className="mt-2 space-y-0.5">
            {status.reasons.map((r, i) => (
              <li key={i} className={`text-xs ${headerText}`}>
                • {r}
              </li>
            ))}
          </ul>
          <div className="mt-2 space-y-1 text-[11px] text-slate-600">
            {/*
              Last-snapshot timestamp — always shown when we have one,
              even on a RED device. Operators want to tell "was healthy,
              went silent" apart from "never enrolled" at a glance, and
              the staleness reason in the list above only shows the
              relative minutes; an absolute timestamp pairs with that
              for proper diagnosis.
            */}
            {status.lastSnapshotAt ? (
              <p>
                Last snapshot{" "}
                <span className="font-medium">
                  {new Date(status.lastSnapshotAt).toLocaleString()}
                </span>
                <span className="text-slate-400">
                  {" · "}
                  {relativeFromNow(status.lastSnapshotAt)}
                </span>
              </p>
            ) : (
              <p className="italic text-slate-400">No snapshot recorded yet.</p>
            )}
            {status.lastTamperAt ? (
              <p>
                Last tamper{" "}
                <span className="font-medium">
                  {new Date(status.lastTamperAt).toLocaleString()}
                </span>
              </p>
            ) : null}
            {enrollmentVerifiedAt ? (
              <p>
                DO confirmed{" "}
                <span className="font-medium">
                  {new Date(enrollmentVerifiedAt).toLocaleString()}
                </span>
                {enrolledManufacturer || enrolledModel ? (
                  <>
                    {" · "}
                    <span className="font-mono">
                      {enrolledManufacturer ?? "?"} {enrolledModel ?? "?"}
                    </span>
                  </>
                ) : null}
              </p>
            ) : null}
            {status.hasDeviceOverride ? (
              <p>
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-blue-800">
                  Custom rules
                </span>
                <span className="ml-2 text-slate-500">
                  this device uses per-device thresholds (see "Restrictions" below)
                </span>
              </p>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * Render "5 min ago" / "2 h ago" / "3 d ago" relative to now. The
 * card refreshes every 15s anyway so we don't bother with a live
 * counter — the timestamp under it is the source of truth, this is
 * just a quick visual cue.
 */
function relativeFromNow(iso: string): string {
  const ms = Date.now() - new Date(iso).valueOf();
  if (ms < 0) return "just now"; // server clock drift
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} d ago`;
}
